import { useState } from "react"
import AvatarStack from "@/components/AvatarStack/AvatarStack"
import DoneRoster from "@/components/DoneRoster/DoneRoster"
import EmptyState from "@/components/EmptyState/EmptyState"
import Pill from "@/components/Pill/Pill"
import RoundHeading from "@/components/RoundHeading/RoundHeading"
import PaperNote from "@/components/PaperNote/PaperNote"
import Spinner from "@/components/Spinner/Spinner"
import Tooltip from "@/components/Tooltip/Tooltip"
import VoteToken from "@/components/VoteToken/VoteToken"
import { PAINS_COPY } from "@/content/pains"
import { useFirestoreWrite } from "@/hooks/useFirestoreWrite"
import { castVote, clearVote } from "@/services/pains"
import { updateParticipant } from "@/services/participants"
import { useSessionStore } from "@/store/sessionStore"
import { usePainVoteDrag } from "./usePainVoteDrag"
import {
  painPositions,
  painRank,
  painsByVotes,
  peopleFor,
  selectAmIDone,
  selectCurrentDoneKey,
  selectMyAuthorId,
  selectPains,
  selectSession,
  votesLeft,
} from "@/store/selectors"
import type { Phase } from "@/types"
import styles from "@/styles/pages/Pains.module.css"

/*
 * The board, face up: reveal, then vote, then the count.
 *
 * One grid serving three phases, because it is one board — the notes must not
 * move between reading them and voting on them, or the room loses track of which
 * is which.
 *
 * The vote is blind: while `phase === "vote"` a voter sees only their own dots
 * and nobody sees a tally. `tally` is a separate phase rather than a toggle for
 * exactly this reason — the count becoming visible is an event the facilitator
 * causes, and the room has to be looking when it happens.
 */

/*
 * Locking the votes is the one write a button here waits on. A vote itself is
 * cast on the note, not on a button, and it lands as an `arrayUnion` the
 * listener reflects in the same frame — a spinner over a sticky note the room
 * is reading would cost more than it told anyone.
 */
const DONE = "done"

interface PainsBoardProps {
  phase: Phase
  canDrive: boolean
  /** The round is being read back. No vote may be cast or taken away. */
  readOnly: boolean
}

export default function PainsBoard({ phase, canDrive, readOnly }: PainsBoardProps) {
  const session = useSessionStore(selectSession)
  const pains = useSessionStore(selectPains)
  const participants = useSessionStore((s) => s.participants)
  const myAuthorId = useSessionStore(selectMyAuthorId)
  const amIDone = useSessionStore(selectAmIDone)
  const doneKey = useSessionStore(selectCurrentDoneKey)
  const currentParticipantId = useSessionStore((s) => s.currentParticipantId)
  const { write, isPending } = useFirestoreWrite()

  const voting = phase === "vote"
  const showTally = phase === "tally"
  // `session` is not known yet on the render that follows a session change —
  // the hook below still has to be called on every render, so this and
  // `left`/`votable` fall back to values that leave it inert until the guard
  // past the early return below has something real to gate on.
  const limit = session?.config.votesPerParticipant ?? 0
  const left = votesLeft(pains, myAuthorId, limit)
  const votable = voting && !readOnly

  const positions = painPositions(pains)

  /*
   * Which tray sticker each of my votes is wearing, keyed by the note it sits
   * on. The sticker's colour comes from this and nothing else, so the one on
   * the note is the one that emptied in the tray, and taking it back refills
   * that same slot.
   *
   * A SLOT EACH, not a position in a list, and that distinction is the whole
   * point. Numbering my votes 0..n-1 by any ordering — board order, or the
   * order I cast them — renumbers every vote above one I take back: two votes
   * down, drop the first, and the second one's sticker changes colour under me.
   * Numbering by board position was worse again, because voting for a note
   * ABOVE one I already backed renumbered both, so the sticker I had just
   * dragged changed colour the moment it landed. Both are the thing the
   * paragraph above promises will not happen.
   *
   * So a slot is HELD until its own vote is taken back, and a new vote takes
   * the lowest slot nothing is holding. Nothing on any document says which
   * slot is which — `voterIds` is an `arrayUnion` per note, recording who
   * backed that note and never in what order I backed the board — so it is
   * remembered here for as long as the screen is up. A reload has nothing to
   * remember and falls back to board order, which is the best a screen that
   * did not watch the votes being cast can do.
   *
   * Kept in state and reconciled during the render that notices a change, the
   * same shape `useSessionView` uses to end a review: `held` is what this
   * render draws from, so a sticker is right on the frame its vote lands
   * rather than on the one after it.
   */
  const [slots, setSlots] = useState<Record<string, number>>({})
  const myVotes = myAuthorId
    ? pains.filter((pain) => pain.voterIds.includes(myAuthorId)).map((pain) => pain.id)
    : []
  const held: Record<string, number> = {}
  const taken = new Set<number>()
  for (const id of myVotes) {
    const slot = slots[id]
    if (slot !== undefined && !taken.has(slot)) {
      held[id] = slot
      taken.add(slot)
    }
  }
  for (const id of myVotes) {
    if (held[id] !== undefined) continue
    let slot = 0
    while (taken.has(slot)) slot++
    held[id] = slot
    taken.add(slot)
  }
  if (
    Object.keys(held).length !== Object.keys(slots).length ||
    myVotes.some((id) => slots[id] !== held[id])
  ) {
    setSlots(held)
  }
  const mySlots = new Map(Object.entries(held).map(([id, slot]) => [id, slot] as const))
  /* The sticker a new vote would spend: the leftmost the tray still has. Also
     the only one that may be grabbed, so what is dragged is always what lands
     — with a budget above two, a slot freed above a held one shows as unspent
     but waits its turn. */
  let nextSlot = 0
  while (taken.has(nextSlot)) nextSlot++

  const painDrag = usePainVoteDrag({
    enabled: votable && !amIDone,
    // Already voted here, or the slot this drag would spend — the tray's
    // leftmost free one, which is the slot `grab` handed the ghost.
    colorIndexFor: (painId) => mySlots.get(painId) ?? nextSlot,
    onDrop: (painId) => {
      if (!session || left === 0 || !myAuthorId) return
      write(() => castVote(session.id, painId, myAuthorId))
    },
  })

  if (!session) return null

  /*
   * Order matters differently per phase. Reading and voting happen in board
   * order — creation order — so the notes never move under the room. Only the
   * tally re-sorts, which is the moment a new order is the point.
   */
  const board = showTally ? painsByVotes(pains) : pains

  /*
   * `leader` names ONE winner, so it may only be set when the vote produced
   * one. `painsByVotes` breaks ties on document id so that every client shows
   * the same order, but that tie-break is presentation: badging its first row
   * would tell the room the vote decided something it did not, and the room
   * cannot see that two notes hold the same count once one of them is wearing
   * a rank tag instead of its tally. A tie tags every note at that band
   * "Tied 1st" and the objective copy says so; the choice is then the
   * facilitator's, at the job gate.
   */
  const topVotes = showTally ? Math.max(0, ...pains.map((pain) => pain.voterIds.length)) : 0
  const tiedAtTop = pains.filter((pain) => pain.voterIds.length === topVotes).length
  const leader = showTally && topVotes > 0 && tiedAtTop === 1 ? board[0] : null

  /*
   * The next two vote bands below the top get their own tags too — "Runner-up"
   * and "Third" — but only the top band's tie is ever named ("Tied 1st"): the
   * mock has no wording for a tied second or third, so two notes sharing the
   * runner-up count both just say "Runner-up".
   */
  const rankTag = (painId: string) => {
    const rank = painRank(pains, painId)
    if (!rank) return null
    if (rank.band === "top") return rank.tied ? PAINS_COPY.tiedFirst : PAINS_COPY.theWinner
    if (rank.band === "runnerUp") return PAINS_COPY.runnerUp
    return PAINS_COPY.third
  }

  /*
   * The vote still SHOWS while the round is read back — the pips, your own
   * marks, the phase the room was on — because that is what someone comes back
   * to check. It just cannot be changed: `votable` gates every path into the
   * write, and the board renders exactly as it did.
   */
  const toggleVote = (painId: string, mine: boolean) => {
    if (!votable || !myAuthorId || amIDone) return
    // A vote is one per pain, so the budget is how many DIFFERENT pains you may
    // back. `arrayUnion` makes a double press a no-op rather than a double vote.
    if (mine) {
      write(() => clearVote(session.id, painId, myAuthorId))
      return
    }
    if (left === 0) return
    write(() => castVote(session.id, painId, myAuthorId))
  }

  /*
   * The facilitator has no participant document, so there is nowhere to write
   * `donePhase` for them — the button is disabled below (`!currentParticipantId`)
   * rather than left clickable and silently doing nothing. The budget still
   * shows: it is a fact of the round the facilitator is watching too, not
   * something that vanishes because they cannot spend it themselves.
   */
  const toggleDone = () => {
    if (!currentParticipantId) return
    write(
      () =>
        updateParticipant(session.id, currentParticipantId, {
          donePhase: amIDone ? null : doneKey,
        }),
      DONE
    )
  }

  const title = showTally
    ? PAINS_COPY.tallyTitle
    : voting
      ? PAINS_COPY.voteBlind
      : PAINS_COPY.revealTitle

  // Same line for whoever is reading it — the room and the facilitator are
  // looking at the same fact, so there is no separate "watching" copy.
  const objective = showTally
    ? leader
      ? PAINS_COPY.tallyBody
      : topVotes === 0
        ? PAINS_COPY.tallyNoVotes
        : PAINS_COPY.tallyTie(tiedAtTop)
    : voting
      ? PAINS_COPY.voteWatchBody
      : PAINS_COPY.revealBody

  return (
    <div className={styles.board}>
      <RoundHeading
        title={title}
        objective={objective}
        /*
         * The live count sits opposite the title, not under the objective — the
         * answer to "where are we" reads as a fact about the room, not as a
         * continuation of the sentence beside it. The roster joins it during the
         * vote, and only for the facilitator: it exists to gate advancing, which
         * is not a decision a voter makes, and "0 of 1 voted" over your own
         * board is just your own unfinished work read back at you.
         */
        aside={
          !showTally ? (
            <div className={styles.headAside}>
              <span className={styles.headCount}>{PAINS_COPY.revealHeadline(pains.length)}</span>
              {voting && canDrive ? <DoneRoster label={PAINS_COPY.votedCount} /> : null}
            </div>
          ) : null
        }
      />

      {pains.length === 0 ? (
        <EmptyState title={PAINS_COPY.emptyBoard} />
      ) : (
        <ul className={styles.grid}>
          {board.map((pain) => {
            const mine = myAuthorId !== null && pain.voterIds.includes(myAuthorId)
            const rank = showTally ? rankTag(pain.id) : null

            /*
             * During the vote only your own mark is shown; the others' avatars
             * would leak the tally the phase exists to hide.
             */
            const voters = showTally ? peopleFor(participants, pain.voterIds) : []

            /*
             * The loaded-by pill is the one badge still worn in the foot row.
             */
            const badge =
              !voting && pain.source === "known" ? (
                <Pill tone="neutral">{PAINS_COPY.loadedBy}</Pill>
              ) : null

            const voteSticker =
              voting && mine ? (
                <VoteToken
                  index={mySlots.get(pain.id) ?? 0}
                  size="lg"
                  label={PAINS_COPY.yourVote}
                />
              ) : null

            return (
              <li
                key={pain.id}
                data-pain-id={pain.id}
                className={votable && painDrag.over === pain.id ? styles.dropTarget : undefined}
              >
                <PaperNote
                  position={positions.get(pain.id) ?? 0}
                  cornerTag={rank}
                  onClick={votable && !amIDone ? () => toggleVote(pain.id, mine) : undefined}
                  title={votable ? (mine ? "Take your vote back" : "Vote for this") : undefined}
                >
                  <p className={styles.text}>{pain.text}</p>

                  {/* Hidden outright on the reveal: "0 vote" still reads as
                   * a count, and no vote has been cast because voting has
                   * not opened yet. Once voting is open the bar comes back
                   * showing only what this voter did — 1 on a note they
                   * backed, 0 otherwise — never the real tally, which the
                   * blind phase exists to hide. */}
                  {showTally || voting || badge ? (
                    <span className={styles.foot}>
                      {showTally || voting ? (
                        <span className={styles.footCount}>
                          {PAINS_COPY.noteVoteCount(
                            showTally ? pain.voterIds.length : mine ? 1 : 0
                          )}
                        </span>
                      ) : null}
                      {voters.length > 0 || badge || voteSticker ? (
                        <span className={styles.footRight}>
                          {voters.length > 0 ? (
                            <span className={styles.footAvatars}>
                              <AvatarStack people={voters} size={28} max={5} />
                            </span>
                          ) : null}
                          {badge}
                          {voteSticker}
                        </span>
                      ) : null}
                    </span>
                  ) : null}
                </PaperNote>
              </li>
            )
          })}
        </ul>
      )}

      {voting ? (
        canDrive ? (
          <div className={`${styles.trayFixed} ${styles.trayFixedFoot}`}>
            <div className={styles.voteTray}>
              {/* Once locked in there is nothing left to spend, so the tokens
               * make way for the status bar the mockup shows on its own — see
               * the guest tray below, which drops the same way. */}
              {amIDone ? null : (
                <div className={styles.tokens}>
                  {Array.from({ length: limit }, (_, index) => {
                    /* A slot is empty while its own vote is on the board, and
                     * refills when that vote is taken back — so the gap in the
                     * tray is always the sticker the note is wearing. Only the
                     * leftmost empty one is grabbable; see `nextSlot`. */
                    const available = !taken.has(index)
                    const grabbable = index === nextSlot
                    return (
                      <span
                        key={index}
                        className={
                          grabbable ? `${styles.tokenCell} ${styles.tokenGrab}` : styles.tokenCell
                        }
                        style={
                          grabbable && painDrag.carrying === index ? { opacity: 0.35 } : undefined
                        }
                        onPointerDown={grabbable ? painDrag.grab(index) : undefined}
                      >
                        <VoteToken index={index} spent={!available} size="lg" />
                      </span>
                    )
                  })}
                </div>
              )}
              <div className={styles.trayFoot}>
                <span className={styles.trayStatus}>
                  <span className={styles.trayDot} aria-hidden="true" />
                  {PAINS_COPY.budget(left)}
                </span>
                {votable ? <span className={styles.trayDivider} aria-hidden="true" /> : null}
                {votable
                  ? (() => {
                      const doneButton = (
                        <button
                          type="button"
                          className={amIDone ? `${styles.done} ${styles.doneOn}` : styles.done}
                          aria-pressed={amIDone}
                          // The bubble carries the full sentence, so it must not be
                          // announced twice — see `Tooltip`.
                          aria-label={amIDone ? PAINS_COPY.votesLockedTooltip : undefined}
                          disabled={
                            !amIDone && (left === limit || isPending(DONE) || !currentParticipantId)
                          }
                          aria-disabled={amIDone || undefined}
                          aria-busy={isPending(DONE) || undefined}
                          onClick={() => {
                            if (amIDone) return
                            toggleDone()
                          }}
                        >
                          {isPending(DONE) ? <Spinner size="sm" /> : null}
                          {amIDone ? PAINS_COPY.lockedIn : PAINS_COPY.imDone}
                        </button>
                      )
                      return amIDone ? (
                        <Tooltip label={PAINS_COPY.votesLockedTooltip} placement="top">
                          {doneButton}
                        </Tooltip>
                      ) : (
                        doneButton
                      )
                    })()
                  : null}
              </div>
            </div>
          </div>
        ) : amIDone ? null : (
          // The budget and the done button are RoundControls' — see the
          // comment above — so once locked in there is nothing left to show
          // here at all.
          <div className={`${styles.trayFixed} ${styles.trayFixedTight}`}>
            <div className={styles.voteTray}>
              <div className={styles.tokens}>
                {Array.from({ length: limit }, (_, index) => {
                  const available = !taken.has(index)
                  const grabbable = index === nextSlot
                  return (
                    <span
                      key={index}
                      className={
                        grabbable ? `${styles.tokenCell} ${styles.tokenGrab}` : styles.tokenCell
                      }
                      style={
                        grabbable && painDrag.carrying === index ? { opacity: 0.35 } : undefined
                      }
                      onPointerDown={grabbable ? painDrag.grab(index) : undefined}
                    >
                      <VoteToken index={index} spent={!available} size="lg" />
                    </span>
                  )
                })}
              </div>
            </div>
          </div>
        )
      ) : null}
    </div>
  )
}
